/**
 * How a notification's action link must be followed (FRONT-1764).
 *
 * A notification action carries an `href` that is authored server-side —
 * either by an admin writing a broadcast (`src/notifications/admin-routes.ts`)
 * or by a system announcement. The validator there accepts two shapes: an
 * absolute `http(s)` URL, or anything starting with `/`. The client then has
 * to decide, per link, whether to call the API, navigate inside the SPA, or
 * hand the browser off to another site entirely — and those are three
 * different mechanisms, not one.
 *
 * Why this exists as its own module rather than an `if` at the call site:
 * react-router 7.18 added client-side navigation-target validation, so
 * `navigate("https://example.com")` now THROWS `External navigation is not
 * allowed` where 7.14 silently landed on the nonsense internal path
 * `/https:/example.com`. The notification mutation has no error handler at
 * either of its call sites (the Today task list and
 * `routes/notifications.tsx` both call `run.mutate(n)` bare — FRONT-1819
 * deleted a third in `routes/security.tsx`), so an uncaught throw
 * there is completely invisible: the button does nothing, and the notification
 * is never resolved. A visibly-wrong page would have become a dead control.
 *
 * The frontend suite runs under plain node with no DOM, so a component test
 * cannot exercise `window.location.assign`. Keeping the DECISION pure and
 * testable here — and leaving only the mechanical hand-off at the call site —
 * is what makes the shipped behaviour assertable at all.
 */

export type NotificationHrefKind =
  /** An Eddy API endpoint the action invokes directly (GET or POST). */
  | "api"
  /** An in-app path the SPA router can navigate to. */
  | "internal"
  /**
   * A desktop-operator run link (FRONT-1990 round 2): `/operator/runs/<uuid>`,
   * exactly — no trailing segments, no non-uuid id. Opens the operator panel
   * in place instead of navigating; anything that doesn't match the pattern
   * exactly falls through to its ordinary classification (`internal`).
   */
  | "operator-run"
  /**
   * A page this origin's server renders, not the SPA (FRONT-3469): a shared
   * app, deck or sketch at `/apps/<slug>/`. The router has no route for it, so
   * it needs a real document load, like `external`.
   */
  | "server-page"
  /** Another origin — needs a real document load, never the SPA router. */
  | "external"
  /**
   * Anything else: a protocol-relative `//host`, a `javascript:` /
   * `data:` / `mailto:` scheme, or a bare relative string. Never followed.
   */
  | "unsupported";

// Anchored so a near-miss — an extra path segment, a non-uuid id — falls
// through to the ordinary "internal" classification instead of silently
// matching a prefix.
const OPERATOR_RUN_HREF =
  /^\/operator\/runs\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

// The same shape the dev proxy forwards to the backend (`frontend/vite.config.ts`): a non-empty first segment.
const SERVER_PAGE_HREF = /^\/apps\/[^/?#]+/;

/**
 * Classify a notification action link.
 *
 * Deliberately strict, and deliberately NOT a mirror of the server-side
 * validator: that one accepts any leading `/`, which lets a
 * protocol-relative `//evil.com` through. Treating such a value as internal
 * would hand react-router a target it rejects; treating it as external would
 * turn an admin broadcast into an off-site redirect with no visible scheme.
 * Refusing it is the only reading that is both safe and honest.
 */
export function classifyNotificationHref(href: string): NotificationHrefKind {
  const value = href.trim();
  if (value === "") return "unsupported";
  // Absolute http(s) first: an external URL never reaches the router.
  if (/^https?:\/\//i.test(value)) return "external";
  // Protocol-relative — external in effect, with the scheme hidden. Refused.
  if (value.startsWith("//")) return "unsupported";
  // Anything not rooted at `/` carries a scheme we do not follow
  // (javascript:, data:, mailto:, tel:) or is a bare relative string.
  if (!value.startsWith("/")) return "unsupported";
  // `/api/x` is called, not navigated to. The trailing slash matters:
  // `/apiary` is an ordinary page, not an endpoint.
  if (value.startsWith("/api/")) return "api";
  if (OPERATOR_RUN_HREF.test(value)) return "operator-run";
  if (SERVER_PAGE_HREF.test(value)) return "server-page";
  return "internal";
}

/**
 * The run id carried by an operator-run action href, or null when the href
 * doesn't match that exact shape. Only meaningful when
 * `classifyNotificationHref(href) === "operator-run"` — a caller that skips
 * the classification check simply gets null for anything else, never a
 * guessed id.
 */
export function operatorRunIdFromHref(href: string): string | null {
  const match = OPERATOR_RUN_HREF.exec(href.trim());
  return match ? match[1] : null;
}
